// Back-in-stock "Notify me".
// The markup lives in an inert <template> (see snippets/nw-notify-me.liquid) so it is
// NOT in the DOM when Swym injects its wishlist button on load — that avoids disturbing
// Swym. A manager clones it into the buy area only when the selected variant is sold out
// (after Swym has already run), and removes it when the variant is in stock. On submit it
// registers a Klaviyo back-in-stock alert for the variant (client-side API).

if (!customElements.get('notify-me-form')) {
  customElements.define(
    'notify-me-form',
    class extends HTMLElement {
      connectedCallback() {
        this.company = this.dataset.klaviyoCompany;
        this.trigger = this.querySelector('.nw-notify__trigger');
        this.panel = this.querySelector('.nw-notify__panel');
        this.emailInput = this.querySelector('.nw-notify__email');
        this.submitBtn = this.querySelector('.nw-notify__submit');
        this.successEl = this.querySelector('.nw-notify__success');
        this.errorEl = this.querySelector('.nw-notify__error');

        this.trigger?.addEventListener('click', () => this.expand());
        this.submitBtn?.addEventListener('click', () => this.submit());
        this.emailInput?.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.submit();
          }
        });
      }

      expand() {
        this.trigger.hidden = true;
        this.panel.hidden = false;
        this.emailInput?.focus();
      }

      showError(msg) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      async submit() {
        const email = (this.emailInput.value || '').trim();
        this.errorEl.hidden = true;
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          this.showError('Please enter a valid email address.');
          this.emailInput.focus();
          return;
        }

        const original = this.submitBtn.textContent;
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = 'Submitting…';

        try {
          const res = await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.company}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
              body: JSON.stringify({
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: {
                    profile: { data: { type: 'profile', attributes: { email } } },
                    channels: ['EMAIL'],
                  },
                  relationships: {
                    variant: {
                      data: {
                        type: 'catalog-variant',
                        id: `$shopify:::$default:::${this.dataset.variantId}`,
                      },
                    },
                  },
                },
              }),
            }
          );

          if (res.ok) {
            this.panel.hidden = true;
            this.successEl.hidden = false;
          } else {
            console.error('Klaviyo back-in-stock error', res.status, await res.text());
            this.showError("Sorry, that didn't work. Please try again.");
          }
        } catch (err) {
          console.error('Klaviyo back-in-stock request failed', err);
          this.showError("Sorry, that didn't work. Please try again.");
        } finally {
          this.submitBtn.disabled = false;
          this.submitBtn.textContent = original;
        }
      }
    }
  );
}

// Manager: clone the template into the buy area only when sold out; remove when in stock.
document.querySelectorAll('template.nw-notify-template').forEach((tmpl) => {
  const section = tmpl.closest('[id^="shopify-section"]') || document;
  const productForm = section.querySelector('product-form-component');
  const buttons = productForm?.querySelector('.product-form-buttons');
  if (!productForm || !buttons) return;

  const show = (variantId) => {
    buttons.classList.add('is-sold-out');
    let notify = productForm.querySelector('notify-me-form');
    if (!notify) {
      notify = tmpl.content.firstElementChild.cloneNode(true);
      notify.dataset.klaviyoCompany = tmpl.dataset.klaviyoCompany || '';
      notify.dataset.variantId = String(variantId || tmpl.dataset.variantId || '');
      buttons.insertAdjacentElement('afterend', notify);
    } else if (variantId) {
      notify.dataset.variantId = String(variantId);
    }
  };

  const hide = () => {
    buttons.classList.remove('is-sold-out');
    productForm.querySelector('notify-me-form')?.remove();
  };

  // Initial state (rendered variant may already be sold out).
  if (tmpl.dataset.initialSoldOut === 'true') show(tmpl.dataset.variantId);

  document.addEventListener('variant:update', (e) => {
    const eventProductId = e.detail?.data?.productId;
    const myProductId = productForm.dataset.productId;
    if (eventProductId && myProductId && String(eventProductId) !== String(myProductId)) return;

    const variant = e.detail?.resource ?? e.detail?.variant;
    if (!variant || variant.available === false) show(variant?.id);
    else hide();
  });
});
